import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { ActionButton, AppModal } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import type { ScheduleResponseRow, ScheduleWorkInterval } from '@/types/api'
import {
  breakOverlapMinutes,
  getDatesInMonthByWeekdays,
  padTimeStr,
  timeToMinutes,
  validateWorkIntervals,
} from '@/pages/schedules/schedule-utils'

const WEEKDAY_LABELS: { d: number; label: string }[] = [
  { d: 1, label: 'Пн' },
  { d: 2, label: 'Вт' },
  { d: 3, label: 'Ср' },
  { d: 4, label: 'Чт' },
  { d: 5, label: 'Пт' },
  { d: 6, label: 'Сб' },
  { d: 0, label: 'Вс' },
]

function intervalsFromRow(existing: ScheduleResponseRow | undefined): ScheduleWorkInterval[] {
  if (!existing) return [{ start_time: '09:00', end_time: '18:00' }]
  if (existing.work_intervals?.length) {
    return existing.work_intervals.map((i) => ({
      start_time: padTimeStr(i.start_time),
      end_time: padTimeStr(i.end_time),
    }))
  }
  if (existing.start_time && existing.end_time) {
    return [
      {
        start_time: padTimeStr(existing.start_time),
        end_time: padTimeStr(existing.end_time),
      },
    ]
  }
  return [{ start_time: '09:00', end_time: '18:00' }]
}

function breakFromRow(existing: ScheduleResponseRow | undefined): { start: string; end: string } {
  if (!existing) return { start: '', end: '' }
  const b = existing.break_intervals?.[0]
  if (b?.start_time && b?.end_time) {
    return { start: padTimeStr(b.start_time), end: padTimeStr(b.end_time) }
  }
  if (existing.break_start && existing.break_end) {
    return { start: padTimeStr(String(existing.break_start)), end: padTimeStr(String(existing.break_end)) }
  }
  return { start: '', end: '' }
}

type Props = {
  opened: boolean
  onClose: () => void
  userId: string
  userName: string
  dateStr: string
  year: number
  month: number
  existing: ScheduleResponseRow | undefined
  onReload: () => Promise<void>
}

export function ScheduleEditModal({
  opened,
  onClose,
  userId,
  userName,
  dateStr,
  year,
  month,
  existing,
  onReload,
}: Props) {
  const [workIntervals, setWorkIntervals] = useState<ScheduleWorkInterval[]>([])
  const [breakStart, setBreakStart] = useState('')
  const [breakEnd, setBreakEnd] = useState('')
  const [dupWeekdays, setDupWeekdays] = useState<Set<number>>(() => new Set())
  const [dupDays, setDupDays] = useState<Set<number>>(() => new Set())
  const [markDelete, setMarkDelete] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!opened) return
    setWorkIntervals(intervalsFromRow(existing))
    const br = breakFromRow(existing)
    setBreakStart(br.start)
    setBreakEnd(br.end)
    setDupWeekdays(new Set())
    setDupDays(new Set())
    setMarkDelete(false)
  }, [opened, existing, dateStr])

  const titleDate = useMemo(() => {
    const [y, m, d] = dateStr.split('-').map(Number)
    const dt = new Date(y, m - 1, d)
    if (Number.isNaN(dt.getTime())) return dateStr
    const dayName = dt.toLocaleDateString('ru-RU', { weekday: 'long' })
    return `${dayName}, ${d} ${dt.toLocaleDateString('ru-RU', { month: 'long' })}`
  }, [dateStr])

  const lastDayOfMonth = useMemo(() => new Date(year, month, 0).getDate(), [year, month])

  const calendarCells = useMemo(() => {
    const first = (new Date(year, month - 1, 1).getDay() + 6) % 7
    const cells: (number | null)[] = []
    for (let i = 0; i < first; i++) cells.push(null)
    for (let d = 1; d <= lastDayOfMonth; d++) cells.push(d)
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }, [year, month, lastDayOfMonth])

  const calendarWeekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

  const toggleWeekday = (d: number) => {
    setDupWeekdays((prev) => {
      const n = new Set(prev)
      if (n.has(d)) n.delete(d)
      else n.add(d)
      return n
    })
  }

  const toggleDupDay = (day: number) => {
    setDupDays((prev) => {
      const n = new Set(prev)
      if (n.has(day)) n.delete(day)
      else n.add(day)
      return n
    })
  }

  const addInterval = () => {
    setWorkIntervals((w) => [...w, { start_time: '09:00', end_time: '18:00' }])
  }

  const removeInterval = (index: number) => {
    setWorkIntervals((w) => (w.length <= 1 ? w : w.filter((_, i) => i !== index)))
  }

  const updateInterval = (index: number, field: 'start_time' | 'end_time', value: string) => {
    setWorkIntervals((w) => {
      const n = [...w]
      n[index] = { ...n[index], [field]: value }
      return n
    })
  }

  const save = useCallback(async () => {
    if (markDelete && existing?.id != null) {
      setSaving(true)
      try {
        await api.scheduleDelete(Number(existing.id))
        showNotification({ title: 'Удалено', message: 'Запись табеля удалена', color: 'green' })
        await onReload()
        onClose()
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : 'Ошибка удаления'
        showNotification({ title: 'Ошибка', message: msg, color: 'red' })
      } finally {
        setSaving(false)
      }
      return
    }

    const wi = workIntervals
      .map((i) => ({
        start_time: padTimeStr(i.start_time),
        end_time: padTimeStr(i.end_time),
      }))
      .filter((i) => i.start_time && i.end_time)

    if (wi.length === 0) {
      showNotification({ title: 'Проверка', message: 'Добавьте хотя бы один рабочий интервал', color: 'yellow' })
      return
    }

    if (!validateWorkIntervals(wi)) {
      showNotification({ title: 'Проверка', message: 'Рабочие интервалы не должны пересекаться', color: 'yellow' })
      return
    }

    const bs = breakStart.trim() ? padTimeStr(breakStart) : ''
    const be = breakEnd.trim() ? padTimeStr(breakEnd) : ''
    const breakIntervals: ScheduleWorkInterval[] =
      bs && be ? [{ start_time: bs, end_time: be }] : []

    const breakWarnings =
      bs && be
        ? breakOverlapMinutes(timeToMinutes(bs), timeToMinutes(be), wi) === 0
        : false

    if (breakWarnings) {
      const ok = await new Promise<boolean>((resolve) => {
        openConfirmModal({
          title: 'Перерыв',
          children: (
            <Text size="sm">
              Перерыв не попадает в рабочие интервалы и не будет учитываться при подсчёте часов. Сохранить?
            </Text>
          ),
          labels: { confirm: 'Сохранить', cancel: 'Отмена' },
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
      if (!ok) return
    }

    const weekdays = [...dupWeekdays]
    const datesByWeekday = getDatesInMonthByWeekdays(year, month, weekdays, dateStr)
    const datesByConcrete = [...dupDays]
      .map((day) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`)
      .filter((d) => d !== dateStr)
    const otherDates = [...new Set([...datesByWeekday, ...datesByConcrete])]

    const updateBody = {
      work_intervals: wi,
      break_intervals: breakIntervals.length ? breakIntervals : ([] as ScheduleWorkInterval[]),
    }

    setSaving(true)
    try {
      if (existing?.id != null) {
        await api.scheduleUpdate(Number(existing.id), updateBody)
      } else {
        await api.scheduleCreate({
          user_id: userId,
          date: dateStr,
          work_intervals: wi,
          break_start: bs && be ? bs : undefined,
          break_end: bs && be ? be : undefined,
        })
      }

      const dateKey = (s: { date: string }) => String(s.date).slice(0, 10)
      let snap = await api.scheduleMonth(userId, year, month)
      let duplicated = 0
      const dupPayload = {
        work_intervals: wi,
        break_intervals: breakIntervals.length ? breakIntervals : ([] as ScheduleWorkInterval[]),
      }
      for (const targetDate of otherDates) {
        try {
          const row = snap.schedules.find((s) => dateKey(s) === targetDate)
          if (row?.id != null) {
            // eslint-disable-next-line no-await-in-loop -- последовательно, как в classic
            await api.scheduleUpdate(Number(row.id), dupPayload)
          } else {
            // eslint-disable-next-line no-await-in-loop
            const created = await api.scheduleCreate({
              user_id: userId,
              date: targetDate,
              work_intervals: wi,
              break_start: bs && be ? bs : undefined,
              break_end: bs && be ? be : undefined,
            })
            snap = { ...snap, schedules: [...snap.schedules, created] }
          }
          duplicated++
        } catch {
          /* skip single day */
        }
      }

      await onReload()
      showNotification({
        title: 'Сохранено',
        message: duplicated > 0 ? `Табель сохранён. Применён ещё к ${duplicated} дн.` : 'Табель сохранён',
        color: 'green',
      })
      onClose()
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка сохранения табеля'
      showNotification({ title: 'Ошибка', message: msg, color: 'red' })
    } finally {
      setSaving(false)
    }
  }, [
    markDelete,
    existing,
    workIntervals,
    breakStart,
    breakEnd,
    dupWeekdays,
    dupDays,
    year,
    month,
    dateStr,
    userId,
    onReload,
    onClose,
  ])

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={null}
      size="lg"
      footer={
        <>
          <ActionButton action="cancel" onClick={onClose} disabled={saving} />
          <Button onClick={() => void save()} loading={saving}>
            {existing?.id != null ? 'Обновить' : 'Добавить'}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <div>
          <Title order={4} mb={4}>
            {titleDate}
          </Title>
          <Text size="sm" c="dimmed">
            {userName}
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            Время начала и конца — в московском поясе (МСК). На сервере факт смены хранится в UTC; план
            сопоставляется с ним по МСК.
          </Text>
        </div>

        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Рабочие интервалы</Text>
            <Button variant="light" size="xs" onClick={addInterval}>
              Добавить интервал
            </Button>
          </Group>
          <Stack gap="sm">
            {workIntervals.map((iv, index) => (
              <Group key={index} align="flex-end" wrap="nowrap">
                <TextInput
                  label="Начало"
                  type="time"
                  value={iv.start_time}
                  onChange={(e) => updateInterval(index, 'start_time', e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <TextInput
                  label="Конец"
                  type="time"
                  value={iv.end_time}
                  onChange={(e) => updateInterval(index, 'end_time', e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <Button
                  variant="default"
                  size="sm"
                  mb={4}
                  disabled={workIntervals.length <= 1}
                  onClick={() => removeInterval(index)}
                >
                  Удалить
                </Button>
              </Group>
            ))}
          </Stack>
        </div>

        <div>
          <Text fw={600} mb="xs">
            Перерыв (опционально)
          </Text>
          <Group wrap="nowrap">
            <TextInput
              label="Начало"
              type="time"
              value={breakStart}
              onChange={(e) => setBreakStart(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <TextInput
              label="Конец"
              type="time"
              value={breakEnd}
              onChange={(e) => setBreakEnd(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              variant="default"
              size="sm"
              mt={24}
              onClick={() => {
                setBreakStart('')
                setBreakEnd('')
              }}
            >
              Очистить
            </Button>
          </Group>
        </div>

        <div>
          <Text fw={600} mb={4}>
            Применить к другим дням месяца
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            По дням недели — тот же график на все совпадения в месяце (текущий день можно дублировать отдельно
            календарём ниже).
          </Text>
          <Group gap="md" wrap="wrap">
            {WEEKDAY_LABELS.map(({ d, label }) => (
              <Checkbox
                key={d}
                label={label}
                checked={dupWeekdays.has(d)}
                onChange={() => toggleWeekday(d)}
              />
            ))}
          </Group>
          <Group mt="xs" gap="xs">
            <Button
              size="xs"
              variant="default"
              onClick={() => setDupWeekdays(new Set([1, 2, 3, 4, 5]))}
            >
              Будние
            </Button>
            <Button size="xs" variant="default" onClick={() => setDupWeekdays(new Set([0, 6]))}>
              Выходные
            </Button>
            <Button size="xs" variant="default" onClick={() => setDupWeekdays(new Set())}>
              Снять
            </Button>
          </Group>
        </div>

        <div>
          <Text fw={600} mb={4}>
            Календарь месяца
          </Text>
          <SimpleGrid cols={7} spacing={4} verticalSpacing={4}>
            {calendarWeekdays.map((w) => (
              <Text key={w} size="xs" ta="center" fw={600} c="dimmed">
                {w}
              </Text>
            ))}
            {calendarCells.map((d, i) =>
              d == null ? (
                <div key={`e-${i}`} />
              ) : (
                <Checkbox
                  key={d}
                  label={String(d)}
                  checked={dupDays.has(d)}
                  onChange={() => toggleDupDay(d)}
                  styles={{
                    label: { fontSize: 12 },
                    body: { alignItems: 'center' },
                  }}
                />
              ),
            )}
          </SimpleGrid>
          <Group mt="xs" gap="xs">
            <Button size="xs" variant="default" onClick={() => setDupDays(new Set([...Array(lastDayOfMonth)].map((_, i) => i + 1)))}>
              Все
            </Button>
            <Button
              size="xs"
              variant="default"
              onClick={() => {
                const s = new Set<number>()
                for (let day = 1; day <= lastDayOfMonth; day++) {
                  const dow = new Date(year, month - 1, day).getDay()
                  if (dow >= 1 && dow <= 5) s.add(day)
                }
                setDupDays(s)
              }}
            >
              Будние
            </Button>
            <Button
              size="xs"
              variant="default"
              onClick={() => {
                const s = new Set<number>()
                for (let day = 1; day <= lastDayOfMonth; day++) {
                  const dow = new Date(year, month - 1, day).getDay()
                  if (dow === 0 || dow === 6) s.add(day)
                }
                setDupDays(s)
              }}
            >
              Выходные
            </Button>
            <Button size="xs" variant="default" onClick={() => setDupDays(new Set())}>
              Снять
            </Button>
          </Group>
        </div>

        {existing?.id != null && (
          <Checkbox
            label="Удалить этот рабочий день"
            checked={markDelete}
            onChange={(e) => setMarkDelete(e.currentTarget.checked)}
          />
        )}

      </Stack>
    </AppModal>
  )
}
